import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import cogoToast from "cogo-toast";
import httpRequest from "../../../ajax";
import axios from "axios";
import Layout from "../../../layout";
import TrackChangesIcon from "@material-ui/icons/TrackChanges";
import StarIcon from "@material-ui/icons/Star";
import AccountBalanceWalletIcon from "@material-ui/icons/AccountBalanceWallet";
import EmojiEventsIcon from "@material-ui/icons/EmojiEvents";
import ArrowForwardIosIcon from "@material-ui/icons/ArrowForwardIos";
import DescriptionIcon from "@material-ui/icons/Description";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import HistoryIcon from "@material-ui/icons/History";
import FlashOnIcon from "@material-ui/icons/FlashOn";
import CloseIcon from "@material-ui/icons/Close";
import { CircularProgress } from "@material-ui/core";
import {
  getJobSeekerPlans,
  subscribeToJobSeekerPlan,
  saveMatchScore as saveMatchScoreAction,
  getMatchScoreHistory as getMatchScoreHistoryAction,
} from "../../../redux/actions/jobSeekerSubscriptionActions";
import "./index.scss";

const JobMatchScore = () => {
  const [activeTab, setActiveTab] = useState("url");
  const [jobUrl, setJobUrl] = useState("");
  const [jobDescription, setJobDescription] = useState("");
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [matchResults, setMatchResults] = useState(null);
  const [error, setError] = useState(null);
  const [plans, setPlans] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyRecords, setHistoryRecords] = useState([]);
  const [historyFetched, setHistoryFetched] = useState(false);
  const [showBuyCreditModal, setShowBuyCreditModal] = useState(false);
  const [buyCreditMethod, setBuyCreditMethod] = useState("wallet");
  const [buyingCredit, setBuyingCredit] = useState(false);
  const [pendingAnalyzeAfterBuy, setPendingAnalyzeAfterBuy] = useState(false);
  const { userData } = useSelector((store) => store.auth);
  const dispatch = useDispatch();
  const history = useHistory();

  const refreshSubscription = async () => {
    try {
      const response = await httpRequest(
        "get",
        "/job-seeker-subscriptions/my-subscription",
      );
      if (response.data.status && response.data.data.subscription) {
        setSubscriptionData(response.data.data.subscription);
        return response.data.data.subscription;
      }
    } catch (err) {
      console.log("No active subscription or error fetching:", err);
    }
    return null;
  };

  useEffect(() => {
    const init = async () => {
      try {
        await refreshSubscription();
        const plansResult = await getJobSeekerPlans(dispatch);
        if (Array.isArray(plansResult?.payload)) {
          setPlans(plansResult.payload);
        }
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [dispatch]);

  const tier = subscriptionData?.tier || "freemium";
  const creditsRemaining = subscriptionData?.credits_remaining || 0;
  const isProHunter = tier === "pro_hunter";
  const isSprintPack = tier === "sprint_pack";
  const isPayPerScan = tier === "pay_per_scan";
  const isCreditBased = isSprintPack || isPayPerScan;
  const isFree = tier === "freemium" || !subscriptionData;
  const hasUsableCredits = isProHunter || (isCreditBased && creditsRemaining > 0);
  const canBuySingleScan = !isProHunter;

  const payPerScanPlan = plans?.find((p) => p.tier === "pay_per_scan");
  const isNaira = (userData?.country || "").toLowerCase().includes("nigeria");
  const singleScanPriceDisplay = payPerScanPlan
    ? isNaira
      ? `₦${Number(payPerScanPlan.price_ngn || 1000).toLocaleString()}`
      : `$${Number(payPerScanPlan.price_usd || 2.99).toFixed(2)}`
    : isNaira
      ? "₦1,000"
      : "$2.99";

  let showUpsell = false;
  let upsellTier = "";
  let upsellMessage = "";

  if (isFree) {
    showUpsell = true;
    upsellTier = "SPRINT PACK";
    upsellMessage = "Get 10 Scan Credits";
  } else if (isCreditBased) {
    showUpsell = true;
    upsellTier = "PRO HUNTER";
    upsellMessage = "Unlock Unlimited Match Scans";
  }
  // Pro Hunter users see no upsell

  const runAnalyze = async (submittedUrl, submittedDescription, currentTab) => {
    setAnalyzing(true);
    setError(null);
    setMatchResults(null);

    try {
      const payload = {
        user_id: userData.id,
        include_culture_fit: false,
      };

      if (currentTab === "url") {
        payload.job_url = submittedUrl;
      } else {
        payload.job_description = submittedDescription;
      }

      const matchScoreApiUrl =
        process.env.REACT_APP_MATCH_SCORE_API_BASE_URL ||
        "https://jobmatch.workbrook.com";
      const response = await axios.post(
        `${matchScoreApiUrl}/api/v1/get-match-score-external`,
        payload,
      );

      if (response.data) {
        setMatchResults(response.data);
        setJobDescription("");

        let creditConsumed = false;
        if (isCreditBased) {
          try {
            await httpRequest(
              "post",
              "/job-seeker-subscriptions/use-credit",
              {
                feature: "job_match_score",
                job_id: null,
                metadata: {
                  job_url: submittedUrl || null,
                  has_description: !!submittedDescription,
                },
              },
            );
            creditConsumed = true;
            await refreshSubscription();
          } catch (creditErr) {
            console.error("Failed to deduct credit:", creditErr);
          }
        }

        try {
          const saveResult = await saveMatchScoreAction(dispatch, {
            source_type: currentTab === "url" ? "url" : "description",
            job_url: currentTab === "url" ? submittedUrl : null,
            job_description:
              currentTab === "description" ? submittedDescription : null,
            job_title: response.data?.job_title || null,
            company_name: response.data?.company_name || null,
            match_score: response.data?.match_score ?? 0,
            recommendation: response.data?.recommendation || null,
            matched_skills: response.data?.matched_skills || [],
            missing_skills: response.data?.missing_skills || [],
            culture_fit: response.data?.culture_fit || {},
            details: response.data?.details || {},
            raw_response: response.data,
            credits_consumed: creditConsumed,
          });

          if (saveResult?.type === "SAVE_MATCH_SCORE" && historyFetched) {
            const newRecord = {
              id: saveResult.payload?.id,
              source_type: currentTab === "url" ? "url" : "description",
              job_url: currentTab === "url" ? submittedUrl : null,
              job_description:
                currentTab === "description" ? submittedDescription : null,
              job_title: response.data?.job_title || null,
              company_name: response.data?.company_name || null,
              match_score: response.data?.match_score ?? 0,
              recommendation: response.data?.recommendation || null,
              matched_skills: response.data?.matched_skills || [],
              missing_skills: response.data?.missing_skills || [],
              culture_fit: response.data?.culture_fit || {},
              details: response.data?.details || {},
              raw_response: response.data,
              credits_consumed: creditConsumed ? 1 : 0,
              date_created: new Date().toISOString(),
            };
            setHistoryRecords((prev) => [newRecord, ...prev]);
          }
        } catch (saveErr) {
          console.error("Failed to save match score history:", saveErr);
        }
      }
    } catch (err) {
      console.error("Match score error:", err);
      setError(
        err.response?.data?.detail?.[0]?.msg ||
          "Failed to analyze job match. Please try again.",
      );
    } finally {
      setAnalyzing(false);
    }
  };

  const handleAnalyze = async () => {
    if (!jobUrl && !jobDescription) {
      setError("Please enter a job URL or paste a job description");
      return;
    }

    if (!hasUsableCredits) {
      setError(
        <span>
          You don&apos;t have an active scan credit.{" "}
          <button
            type="button"
            className="jms-error-cta"
            onClick={() => setShowBuyCreditModal(true)}
          >
            Buy 1 Scan ({singleScanPriceDisplay})
          </button>{" "}
          or{" "}
          <a
            href="/my-subscriptions"
            style={{
              color: "#991b1b",
              fontWeight: 700,
              textDecoration: "underline",
            }}
          >
            subscribe
          </a>{" "}
          for more.
        </span>,
      );
      return;
    }

    await runAnalyze(jobUrl, jobDescription, activeTab);
  };

  const toggleHistory = async () => {
    const nextOpen = !historyOpen;
    setHistoryOpen(nextOpen);
    if (nextOpen && !historyFetched) {
      setHistoryLoading(true);
      try {
        const result = await getMatchScoreHistoryAction(dispatch, 1, 20);
        if (result?.type === "GET_MATCH_SCORE_HISTORY") {
          setHistoryRecords(result.payload?.data || []);
          setHistoryFetched(true);
        }
      } finally {
        setHistoryLoading(false);
      }
    }
  };

  const viewHistoryRecord = (record) => {
    const rehydrated = record?.raw_response || {
      match_score: record?.match_score,
      recommendation: record?.recommendation,
      matched_skills: record?.matched_skills,
      missing_skills: record?.missing_skills,
      culture_fit: record?.culture_fit,
      details: record?.details,
    };
    setMatchResults(rehydrated);
    setError(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleBuyCredit = async () => {
    if (!payPerScanPlan) {
      cogoToast.error("Single scan option is unavailable right now.");
      return;
    }
    setBuyingCredit(true);
    try {
      const result = await subscribeToJobSeekerPlan(dispatch, {
        plan_id: payPerScanPlan.id,
        payment_method: buyCreditMethod,
      });

      if (result?.type === "ERROR") {
        cogoToast.error(
          typeof result.payload === "string"
            ? result.payload
            : "Purchase failed. Please try again.",
        );
        return;
      }

      const payload = result?.payload;

      if (buyCreditMethod !== "wallet") {
        if (payload?.requires_payment) {
          cogoToast.info(
            "Redirecting to payment... this flow will complete in the payment gateway.",
          );
        } else {
          cogoToast.success(
            "Purchase initiated. Please complete payment at the gateway.",
          );
        }
        setShowBuyCreditModal(false);
        return;
      }

      if (payload?.status) {
        cogoToast.success("Scan credit added to your account.");
        setShowBuyCreditModal(false);
        await refreshSubscription();

        if (pendingAnalyzeAfterBuy && (jobUrl || jobDescription)) {
          setPendingAnalyzeAfterBuy(false);
          await runAnalyze(jobUrl, jobDescription, activeTab);
        }
      } else if (payload?.code === "insufficient_balance") {
        cogoToast.error(
          payload?.message || "Insufficient wallet balance. Please fund your wallet.",
        );
      } else {
        cogoToast.error(payload?.message || "Purchase failed.");
      }
    } catch (err) {
      console.error("Buy credit error:", err);
      cogoToast.error("Unable to complete purchase. Please try again.");
    } finally {
      setBuyingCredit(false);
    }
  };

  const formatHistoryDate = (value) => {
    if (!value) return "";
    try {
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return value;
      return d.toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
      });
    } catch (e) {
      return value;
    }
  };

  // Show loading state while fetching subscription
  if (loading) {
    return (
      <Layout>
        <div className="jms-loading-container">
          <CircularProgress size={60} style={{ color: "#1d4ed8" }} />
          <p className="jms-loading-text">Loading your subscription details...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="jms-wrapper">
        <div className="jms-page">

        {/* ── Header ── */}
        <div className="jms-header">
          <h2 className="jms-title">Job Match Score</h2>
          <p className="jms-subtitle">Compare your CV against any live job posting instantly.</p>
          <p className="jms-pricing">
            {isFree && (
              <>
                Premium Analysis:{" "}
                <a href="/my-subscriptions">Get Sprint Pack (10 credits) or Pro Hunter (unlimited)</a>{" "}
                — or{" "}
                <button
                  type="button"
                  className="jms-link-btn"
                  onClick={() => setShowBuyCreditModal(true)}
                >
                  buy 1 scan for {singleScanPriceDisplay}
                </button>
                .
              </>
            )}
            {isSprintPack && (
              <>
                Current Plan: <strong>Sprint Pack</strong> —{" "}
                <a href="/my-subscriptions">Upgrade to Pro Hunter for unlimited scans.</a>
              </>
            )}
            {isPayPerScan && (
              <>
                Current Plan: <strong>Pay Per Scan</strong> — {creditsRemaining} credit{creditsRemaining === 1 ? "" : "s"} remaining.{" "}
                <a href="/my-subscriptions">Upgrade for more.</a>
              </>
            )}
            {isProHunter && (
              <>
                Current Plan: <strong style={{ color: "#1d4ed8" }}>Pro Hunter</strong> — Unlimited scans active!
              </>
            )}
          </p>
        </div>

        {/* ── Update Resume CTA ── */}
        <div
          className="jms-resume-cta"
          onClick={() => history.push("/profile/edit/resume")}
        >
          <div className="jms-resume-cta-left">
            <div className="jms-resume-cta-icon">
              <DescriptionIcon style={{ fontSize: 20 }} />
            </div>
            <div className="jms-resume-cta-text">
              <span className="jms-resume-cta-title">Keep your resume up to date</span>
              <span className="jms-resume-cta-sub">
                Accurate resumes produce more accurate match scores.
              </span>
            </div>
          </div>
          <ArrowForwardIosIcon className="jms-resume-cta-arrow" style={{ fontSize: 14 }} />
        </div>

        {/* ── Pro Upsell Banner ── */}
        {showUpsell && (
          <div
            className="jms-pro-banner"
            onClick={() => history.push("/my-subscriptions")}
          >
            <div className="jms-pro-left">
              <div className="jms-pro-badge">
                <StarIcon style={{ fontSize: 11 }} />
                <span>{upsellTier}</span>
              </div>
              <div className="jms-pro-text">
                <span className="jms-pro-exclusive">EXCLUSIVE ACCESS</span>
                <span className="jms-pro-cta">{upsellMessage}</span>
              </div>
            </div>
            <ArrowForwardIosIcon className="jms-pro-arrow" style={{ fontSize: 15 }} />
          </div>
        )}

        {/* ── Buy 1 Scan CTA (one-off) ── */}
        {canBuySingleScan && (
          <button
            type="button"
            className="jms-buy-credit-btn"
            onClick={() => setShowBuyCreditModal(true)}
            disabled={buyingCredit}
          >
            <FlashOnIcon style={{ fontSize: 17, marginRight: 6 }} />
            {isFree
              ? `Buy 1 Scan — ${singleScanPriceDisplay}`
              : `Top up 1 scan credit — ${singleScanPriceDisplay}`}
          </button>
        )}

        {/* ── Tabs + Input Card ── */}
        <div className="jms-card">
          <div className="jms-custom-tabs">
            <div
              className={`jms-custom-tab ${activeTab === "description" ? "jms-custom-tab-active" : ""}`}
              onClick={() => setActiveTab("description")}
            >
              <span className="jms-custom-tab-label">
                Paste Job Description
                <span className="jms-tab-badge">Recommended</span>
              </span>
            </div>
            <div
              className={`jms-custom-tab ${activeTab === "url" ? "jms-custom-tab-active" : ""}`}
              onClick={() => setActiveTab("url")}
            >
              Paste Job URL
            </div>
          </div>

          <div className="jms-input-wrap">
            <label className="jms-input-label">
              {activeTab === "url" ? "ENTER JOB LINK" : "PASTE JOB DESCRIPTION"}
            </label>
            {activeTab === "url" ? (
              <input
                type="url"
                className="jms-url-input"
                placeholder="https://www.linkedin.com/jobs/view/..."
                value={jobUrl}
                onChange={(e) => setJobUrl(e.target.value)}
                disabled={analyzing}
              />
            ) : (
              <textarea
                className="jms-textarea"
                placeholder="Paste the full job description here..."
                value={jobDescription}
                onChange={(e) => setJobDescription(e.target.value)}
                rows={5}
                disabled={analyzing}
              />
            )}
          </div>

          {error && (
            <div className="jms-error-message">
              {error}
            </div>
          )}

          <button 
            className="jms-analyze-btn" 
            onClick={handleAnalyze}
            disabled={analyzing || (!jobUrl && !jobDescription)}
          >
            {analyzing ? (
              <>
                <CircularProgress size={17} style={{ color: "#fff", marginRight: 8 }} />
                Analyzing...
              </>
            ) : (
              <>
                <TrackChangesIcon style={{ fontSize: 17, marginRight: 8 }} />
                Match and Analyze
              </>
            )}
          </button>
        </div>

        {/* ── Match Results ── */}
        {matchResults && (
          <div className="jms-results-container">
            <div className="jms-results-header">
              <h3>Match Analysis Results</h3>
            </div>

            {/* Match Score Circle */}
            <div className="jms-score-circle">
              <div className="jms-circle-wrapper">
                <svg className="jms-circle-svg" viewBox="0 0 120 120">
                  <circle
                    className="jms-circle-bg"
                    cx="60"
                    cy="60"
                    r="54"
                  />
                  <circle
                    className="jms-circle-progress"
                    cx="60"
                    cy="60"
                    r="54"
                    style={{
                      strokeDasharray: `${(matchResults.match_score / 100) * 339.292} 339.292`,
                    }}
                  />
                </svg>
                <div className="jms-score-text">
                  <span className="jms-score-number">{matchResults.match_score}%</span>
                  <span className="jms-score-label">Match Score</span>
                </div>
              </div>
            </div>

            {/* Recommendation */}
            <div className="jms-recommendation">
              <h4>Recommendation</h4>
              <p>{matchResults.recommendation}</p>
            </div>

            {/* Skills Section */}
            {(matchResults.matched_skills?.length > 0 || matchResults.missing_skills?.length > 0) && (
              <div className="jms-skills-section">
                {matchResults.matched_skills?.length > 0 && (
                  <div className="jms-skills-matched">
                    <h4>✓ Matched Skills ({matchResults.matched_skills.length})</h4>
                    <div className="jms-skills-list">
                      {matchResults.matched_skills.map((skill, idx) => (
                        <span key={idx} className="jms-skill-tag jms-skill-matched">
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {matchResults.missing_skills?.length > 0 && (
                  <div className="jms-skills-missing">
                    <h4>⚠ Missing Skills ({matchResults.missing_skills.length})</h4>
                    <div className="jms-skills-list">
                      {matchResults.missing_skills.map((skill, idx) => (
                        <span key={idx} className="jms-skill-tag jms-skill-missing">
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Culture Fit */}
            {matchResults.culture_fit && Object.keys(matchResults.culture_fit).length > 0 && (
              <div className="jms-culture-fit">
                <h4>🎯 Culture Fit Analysis</h4>
                <div className="jms-culture-grid">
                  {Object.entries(matchResults.culture_fit).map(([key, value]) => (
                    <div key={key} className="jms-culture-item">
                      <span className="jms-culture-key">{key}</span>
                      <span className="jms-culture-value">{JSON.stringify(value)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Details */}
            {matchResults.details && Object.keys(matchResults.details).length > 0 && (
              <div className="jms-details">
                <h4>📋 Detailed Breakdown</h4>
                <div className="jms-details-grid">
                  {Object.entries(matchResults.details).map(([key, value]) => (
                    <div key={key} className="jms-detail-card">
                      <div className="jms-detail-header">
                        <span className="jms-detail-title">{key}</span>
                        {value?.score !== undefined && (
                          <span className="jms-detail-score">
                            {value.score}/{value.max || 100}
                          </span>
                        )}
                      </div>
                      {value?.reason && (
                        <p className="jms-detail-reason">{value.reason}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Previous Scans (collapsible) ── */}
        <div className="jms-history-wrap">
          <button
            type="button"
            className="jms-history-toggle"
            onClick={toggleHistory}
          >
            <span className="jms-history-toggle-left">
              <HistoryIcon style={{ fontSize: 18, marginRight: 8 }} />
              Previous Scans
            </span>
            {historyOpen ? (
              <ExpandLessIcon style={{ fontSize: 20 }} />
            ) : (
              <ExpandMoreIcon style={{ fontSize: 20 }} />
            )}
          </button>

          {historyOpen && (
            <div className="jms-history-body">
              {historyLoading && (
                <div className="jms-history-loading">
                  <CircularProgress size={22} style={{ color: "#1d4ed8" }} />
                  <span>Loading your previous scans…</span>
                </div>
              )}

              {!historyLoading && historyRecords.length === 0 && (
                <div className="jms-history-empty">
                  You haven&apos;t run any match score analyses yet. Your saved scans will appear here.
                </div>
              )}

              {!historyLoading && historyRecords.length > 0 && (
                <ul className="jms-history-list">
                  {historyRecords.map((record) => {
                    const score = Number(record.match_score || 0);
                    const label =
                      record.job_title ||
                      record.company_name ||
                      (record.source_type === "url"
                        ? record.job_url
                        : "Pasted job description");
                    return (
                      <li key={record.id} className="jms-history-item">
                        <div
                          className={`jms-history-score ${score >= 70 ? "good" : score >= 50 ? "ok" : "low"}`}
                        >
                          {Math.round(score)}%
                        </div>
                        <div className="jms-history-meta">
                          <span className="jms-history-title" title={label}>
                            {label}
                          </span>
                          <span className="jms-history-date">
                            {formatHistoryDate(record.date_created)}
                          </span>
                        </div>
                        <button
                          type="button"
                          className="jms-history-view"
                          onClick={() => viewHistoryRecord(record)}
                        >
                          View
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}
        </div>

        {/* ── Bottom Stats ── */}
        <div className="jms-stats-grid">
          <div className="jms-stat-card jms-stat-blue">
            <div className="jms-stat-icon">
              <AccountBalanceWalletIcon style={{ fontSize: 20 }} />
            </div>
            <div className="jms-stat-body">
              <span className="jms-stat-label">CURRENT PLAN</span>
              {isFree && (
                <>
                  <p className="jms-stat-value">Free Tier (No Credits)</p>
                  <a href="/my-subscriptions" className="jms-stat-link">
                    Subscribe to Start Scanning
                  </a>
                </>
              )}
              {isSprintPack && (
                <>
                  <p className="jms-stat-value">
                    Sprint Pack{subscriptionData?.plan?.price_ngn ? ` (₦${Number(subscriptionData.plan.price_ngn).toLocaleString()})` : ""}
                  </p>
                  <a href="/my-subscriptions" className="jms-stat-link">
                    Upgrade to Pro Hunter
                  </a>
                </>
              )}
              {isPayPerScan && (
                <>
                  <p className="jms-stat-value">Pay Per Scan</p>
                  <a href="/my-subscriptions" className="jms-stat-link">
                    Upgrade to Sprint Pack or Pro Hunter
                  </a>
                </>
              )}
              {isProHunter && (
                <>
                  <p className="jms-stat-value">
                    Pro Hunter{subscriptionData?.plan?.price_ngn ? ` (₦${Number(subscriptionData.plan.price_ngn).toLocaleString()}/mo)` : ""}
                  </p>
                  <a href="/my-subscriptions" className="jms-stat-link">
                    Manage Subscription
                  </a>
                </>
              )}
            </div>
          </div>

          <div className="jms-stat-card jms-stat-amber">
            <div className="jms-stat-icon">
              <EmojiEventsIcon style={{ fontSize: 20 }} />
            </div>
            <div className="jms-stat-body">
              <span className="jms-stat-label">SCAN CREDITS</span>
              {isFree && (
                <>
                  <p className="jms-stat-value">No credits available</p>
                  <p className="jms-stat-remaining">
                    Subscribe to get credits
                  </p>
                </>
              )}
              {isSprintPack && (
                <>
                  <p className="jms-stat-value">1 credit per scan</p>
                  <p className="jms-stat-remaining">
                    Credits remaining: <strong>{creditsRemaining}</strong>
                  </p>
                </>
              )}
              {isPayPerScan && (
                <>
                  <p className="jms-stat-value">1 credit per scan</p>
                  <p className="jms-stat-remaining">
                    Credits remaining: <strong>{creditsRemaining}</strong>
                  </p>
                </>
              )}
              {isProHunter && (
                <>
                  <p className="jms-stat-value">Unlimited scans</p>
                  <p className="jms-stat-remaining">
                    <strong style={{ color: "#15803d" }}>∞ Unlimited</strong>
                  </p>
                </>
              )}
            </div>
          </div>
        </div>

        </div>
      </div>

      {/* ── Buy 1 Scan Credit Modal ── */}
      {showBuyCreditModal && (
        <div
          className="jms-modal-overlay"
          onClick={() => {
            if (!buyingCredit) {
              setShowBuyCreditModal(false);
              setPendingAnalyzeAfterBuy(false);
            }
          }}
        >
          <div
            className="jms-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="jms-modal-header">
              <h3 className="jms-modal-title">Buy 1 Scan Credit</h3>
              <button
                type="button"
                className="jms-modal-close"
                onClick={() => {
                  if (!buyingCredit) {
                    setShowBuyCreditModal(false);
                    setPendingAnalyzeAfterBuy(false);
                  }
                }}
                aria-label="Close"
              >
                <CloseIcon style={{ fontSize: 20 }} />
              </button>
            </div>

            <p className="jms-modal-sub">
              Get a single Job Match Score scan for{" "}
              <strong>{singleScanPriceDisplay}</strong>. No subscription required.
            </p>

            <div className="jms-modal-section-label">Payment method</div>
            <div className="jms-pay-methods">
              {[
                { id: "wallet", label: "Wallet" },
                { id: "stripe", label: "Card (Stripe)" },
                { id: "jarapay", label: "JaraPay" },
              ].map((opt) => (
                <label
                  key={opt.id}
                  className={`jms-pay-method ${buyCreditMethod === opt.id ? "active" : ""}`}
                >
                  <input
                    type="radio"
                    name="jms-pay-method"
                    value={opt.id}
                    checked={buyCreditMethod === opt.id}
                    onChange={() => setBuyCreditMethod(opt.id)}
                  />
                  <span>{opt.label}</span>
                </label>
              ))}
            </div>

            {(jobUrl || jobDescription) && (
              <label className="jms-modal-autorun">
                <input
                  type="checkbox"
                  checked={pendingAnalyzeAfterBuy}
                  onChange={(e) => setPendingAnalyzeAfterBuy(e.target.checked)}
                  disabled={buyingCredit || buyCreditMethod !== "wallet"}
                />
                <span>
                  Run the match score immediately after purchase
                  {buyCreditMethod !== "wallet" ? " (wallet only)" : ""}
                </span>
              </label>
            )}

            <div className="jms-modal-actions">
              <button
                type="button"
                className="jms-modal-secondary"
                onClick={() => {
                  setShowBuyCreditModal(false);
                  setPendingAnalyzeAfterBuy(false);
                }}
                disabled={buyingCredit}
              >
                Cancel
              </button>
              <button
                type="button"
                className="jms-modal-primary"
                onClick={handleBuyCredit}
                disabled={buyingCredit || !payPerScanPlan}
              >
                {buyingCredit ? (
                  <>
                    <CircularProgress
                      size={15}
                      style={{ color: "#fff", marginRight: 8 }}
                    />
                    Processing…
                  </>
                ) : (
                  `Pay ${singleScanPriceDisplay}`
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default JobMatchScore;